import { url } from '../../../api';
import { apiClient } from '../../../api/client';
import { getAuthToken } from '../../../utils/authTokens';
import { coffeeShopPaths } from '../api/paths';

// raw Google Places candidate returned by /shops/search-google
export interface GoogleCandidate {
    place_id: string;
    name: string;
    geometry: { location: { lat: number; lng: number } };
    vicinity?: string;
    formatted_address?: string;
    rating?: number;
    price_level?: number;
}

// shop shape consumed by the nearby list + select handlers
export interface NearbyCoffeeShop {
    _id?: string;
    name: string;
    place_id?: string;
    status?: string;
    coordinates: { lat: number; lng: number };
    distance: number;
    address: string;
    rating?: number;
    price_level?: number;
    types?: string[];
}

/**
 * Search Google Places by free-text name, biased to the user's location.
 * Returns raw candidates (no distance — compute client-side from coords).
 */
export async function searchGoogleShops(
    query: string,
    lat: number,
    lng: number
): Promise<GoogleCandidate[]> {
    const res = await apiClient.post(
        `${url}${coffeeShopPaths.searchGoogle()}`,
        { query: query.trim(), lat, lng },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
    );
    return res.data?.candidates ?? [];
}

/**
 * Propose a Google candidate as a coffee shop and return it in NearbyCoffeeShop
 * shape. Re-proposing an existing place_id silently returns the existing doc.
 */
export async function proposeGoogleCandidate(
    c: GoogleCandidate,
    opts: { city?: string; country?: string; clusterId?: string }
): Promise<NearbyCoffeeShop | null> {
    const res = await apiClient.post(
        `${url}${coffeeShopPaths.propose()}`,
        {
            name: c.name,
            lat: c.geometry.location.lat,
            lng: c.geometry.location.lng,
            address: c.formatted_address || c.vicinity,
            placeId: c.place_id,
            city: opts.city,
            country: opts.country,
            clusterId: opts.clusterId
        },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
    );
    const shop = res.data?.coffeeShop;
    if (!shop) return null;
    return {
        _id: shop._id,
        name: shop.name,
        place_id: shop.place_id,
        coordinates: shop.coordinates,
        distance: 0,
        address: shop.address,
        rating: shop.rating,
        price_level: shop.price_level
    };
}

/**
 * Propose a manually-entered shop (no Google place_id) — lands in the review
 * queue. Returns it in NearbyCoffeeShop shape.
 */
export async function proposeManualShop(
    fields: { name: string; lat: number; lng: number; address: string; city?: string; country?: string; clusterId?: string }
): Promise<NearbyCoffeeShop | null> {
    const res = await apiClient.post(
        `${url}${coffeeShopPaths.propose()}`,
        {
            name: fields.name,
            lat: fields.lat,
            lng: fields.lng,
            address: fields.address,
            city: fields.city,
            country: fields.country,
            clusterId: fields.clusterId
        },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
    );
    const shop = res.data?.coffeeShop;
    if (!shop) return null;
    return {
        _id: shop._id,
        name: shop.name,
        place_id: shop.place_id,
        coordinates: shop.coordinates,
        distance: 0,
        address: shop.address
    };
}
